import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    createGrant,
    estimateGrantImpact,
    groupKeyId,
    listAccessChanges,
    listAccessDenials,
    listGrantRoles,
    listGrantGroups,
    listGrantViewers,
    listGrants,
    newOperationId,
    revokeGrant,
    updateGrantScope,
} from "../../lib/accessClient.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { hasSensitivePermission, invalidateMyPermissions, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { formatCompact } from "../../lib/renderers.js";
import { roleLabel } from "../../lib/userRoles.js";
import ConfirmDialog from "../ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

const NOTE_CLASS = "text-[11px] leading-[1.5] text-[color:var(--muted)]";
const EYEBROW_CLASS = "text-[11px] font-medium text-[color:var(--muted)]";

const PAGE_SIZE = 25;
const PROVIDER_STORAGE = {
    ENTRA_ID: "entra_id",
    OKTA: "okta",
    GOOGLE: "google_workspace",
    DIRECTORY_PROVIDER_TYPE_ENTRA_ID: "entra_id",
    DIRECTORY_PROVIDER_TYPE_OKTA: "okta",
    DIRECTORY_PROVIDER_TYPE_GOOGLE_WORKSPACE: "google_workspace",
};

const buttonClass = "min-h-[36px] rounded-[4px] border border-[color:var(--line)] px-[10px] text-[11px] text-[color:var(--muted)] hover:border-[color:var(--green)] hover:text-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer";
const primaryButtonClass = `${buttonClass} border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]`;
const destructiveButtonClass = `${buttonClass} hover:border-[color:var(--red,#c0392b)] hover:text-[color:var(--red,#c0392b)] focus-visible:border-[color:var(--red,#c0392b)] focus-visible:text-[color:var(--red,#c0392b)]`;
const inputClass = "h-[38px] rounded-[4px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[9px] text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--green)]";

function useLatestRef(value) {
    const ref = useRef(value);
    ref.current = value;
    return ref;
}

function wireDirectoryGroup(group) {
    return {
        sourceProvider: PROVIDER_STORAGE[group.sourceProvider] ?? group.sourceProvider,
        integrationId: group.integrationId ?? "",
        sourceId: group.sourceId ?? "",
    };
}

function dateTime(ms) {
    if (!ms) return "Unknown time";
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? "Unknown time" : date.toLocaleString();
}

function Panel({ eyebrow, title, action, busy = false, children }) {
    return (
        <section
            aria-busy={busy}
            className="grid min-w-0 gap-[16px] rounded-[4px] bg-[color:var(--bg)] py-[16px]"
        >
            <header className="flex min-w-0 items-start justify-between gap-[12px]">
                <div className="grid min-w-0 gap-[2px]">
                    {eyebrow ? <span className={EYEBROW_CLASS}>{eyebrow}</span> : null}
                    <h2 className="m-0 text-[18px] leading-[1.3] font-medium tracking-[-0.03em] text-[color:var(--ink)]">{title}</h2>
                </div>
                {action}
            </header>
            {children}
        </section>
    );
}

function Badge({ tone = "neutral", children }) {
    const toneClass = tone === "danger"
        ? "access-badge--danger"
        : tone === "warn"
            ? "access-badge--warn"
            : "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)]";
    return <span className={`inline-flex rounded-[4px] border px-[6px] py-[2px] text-[9px] uppercase tracking-[0.07em] ${toneClass}`}>{children}</span>;
}

function ResponsiveField({ label, children, className = "", valueClassName = "" }) {
    return (
        <div className={`grid min-w-0 grid-cols-[104px_minmax(0,1fr)] items-start gap-[8px] @min-[880px]:block ${className}`}>
            <span className="pt-[1px] text-[11px] font-medium text-[color:var(--muted)] @min-[880px]:hidden">{label}</span>
            <div className={`min-w-0 ${valueClassName}`}>{children}</div>
        </div>
    );
}

function LoadingMore({ loading, nextPageToken, shown, total, totalCapped = false, onLoad }) {
    if (!nextPageToken) return null;
    return (
        <button type="button" className={buttonClass} onClick={onLoad} disabled={loading}>
            {loading ? "Loading..." : totalCapped
                ? `Load more (${formatCompact(shown)} loaded)`
                : `Load more (${formatCompact(shown)} of ${formatCompact(total)})`}
        </button>
    );
}

function PersonCount({ value, label, plural = `${label}s` }) {
    const accessibleLabel = `${formatCompact(value)} ${Number(value) === 1 ? label : plural}`;
    return (
        <span className="inline-flex items-center gap-[4px] tabular-nums" aria-label={accessibleLabel}>
            <svg viewBox="0 0 16 16" aria-hidden="true" className="h-[11px] w-[11px]" fill="none" stroke="currentColor" strokeWidth="1.4">
                <circle cx="8" cy="5" r="2.2" />
                <path d="M3.8 13c.4-2.5 1.8-3.8 4.2-3.8s3.8 1.3 4.2 3.8" />
            </svg>
            {formatCompact(value)}
        </span>
    );
}

function countNoun(value, singular, plural = `${singular}s`) {
    return `${formatCompact(value)} ${Number(value) === 1 ? singular : plural}`;
}

function DrilldownList({ kind, grant }) {
    const capsRef = useLatestRef(useMyPermissions());
    const isDenials = kind === "denials";
    const viewerId = grant.grantee.type === "viewer" ? grant.grantee.viewerUserId : "";
    const [state, setState] = useState({ loading: true, rows: [], next: "", total: 0, totalCapped: false, error: "" });
    const load = useCallback(async (token = "") => {
        if (isDenials && !viewerId) {
            setState({ loading: false, rows: [], next: "", total: 0, totalCapped: false, error: "" });
            return;
        }
        setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const page = isDenials
                ? await listAccessDenials(viewerId, { pageSize: 20, pageToken: token })
                : await listAccessChanges(grant.id, { pageSize: 20, pageToken: token });
            const rows = isDenials ? page.denials : page.changes;
            setState((current) => ({
                loading: false,
                rows: token ? [...current.rows, ...rows] : rows,
                next: page.nextPageToken,
                total: page.totalCount,
                totalCapped: Boolean(page.totalCountCapped),
                error: "",
            }));
        } catch (error) {
            setState((current) => ({ ...current, loading: false, error: denialOrRawMessage(error, capsRef.current) }));
        }
    }, [capsRef, grant.id, isDenials, viewerId]);
    useEffect(() => { load(); }, [load]);

    if (isDenials && !viewerId) {
        return <span className={NOTE_CLASS}>Recent failed access checks are available for user grants only.</span>;
    }
    return (
        <div aria-busy={state.loading} className="grid gap-[6px]">
            <span className="text-[11px] text-[color:var(--muted)]">
                Showing {formatCompact(state.rows.length)} of {formatCompact(state.total)}{state.totalCapped ? "+" : ""}
            </span>
            {state.error ? <span className="text-[11px] text-red-300">{state.error}</span> : null}
            {state.rows.length ? (
                <div className="border-y border-[color:var(--line-soft)]">
                    <div className="hidden @min-[880px]:grid @min-[880px]:grid-cols-[minmax(130px,0.7fr)_minmax(160px,1fr)_minmax(120px,0.7fr)] gap-[8px] px-[6px] py-[5px] text-[11px] font-medium text-[color:var(--muted)]">
                        <span>Time</span>
                        <span>{isDenials ? "Request" : "Change"}</span>
                        <span>{isDenials ? "Reason" : "Changed by"}</span>
                    </div>
                    {state.rows.map((row, index) => (
                        <div key={row.changeId || `${row.occurredAt}-${row.rpcName}-${index}`} className="grid gap-[6px] border-t border-[color:var(--line-soft)] px-[6px] py-[6px] text-[11px] @min-[880px]:grid-cols-[minmax(130px,0.7fr)_minmax(160px,1fr)_minmax(120px,0.7fr)]">
                            <ResponsiveField label="Time"><span className="text-[color:var(--muted)]">{dateTime(row.occurredAt)}</span></ResponsiveField>
                            <ResponsiveField label={isDenials ? "Request" : "Change"}><span className="text-[color:var(--ink)]">{isDenials ? row.rpcName : `${row.action} - ${row.roleSlug}`}</span></ResponsiveField>
                            <ResponsiveField label={isDenials ? "Reason" : "Changed by"}><span className="text-[color:var(--muted)]">{isDenials ? `${row.reason}${row.coalescedCount > 1 ? ` - x${row.coalescedCount}` : ""}` : row.actorDisplay}</span></ResponsiveField>
                        </div>
                    ))}
                </div>
            ) : null}
            {!state.loading && !state.rows.length ? <span className={NOTE_CLASS}>{isDenials ? "No recent failed access checks for this user." : "No changes have been recorded for this grant."}</span> : null}
            <LoadingMore loading={state.loading} nextPageToken={state.next} shown={state.rows.length} total={state.total} totalCapped={state.totalCapped} onLoad={() => load(state.next)} />
        </div>
    );
}

function GrantDrilldown({ grant }) {
    const [kind, setKind] = useState("audit");
    const isGroupGrant = grant.grantee.type === "group";
    return (
        <div className="grid gap-[8px] border-t border-[color:var(--line)] pt-[9px]">
            {isGroupGrant ? (
                <>
                    <span className={EYEBROW_CLASS}>Audit trail</span>
                    <DrilldownList key={`${grant.id}-audit`} kind="audit" grant={grant} />
                </>
            ) : (
                <>
                    <div className="flex gap-[6px]" role="tablist" aria-label={`Grant history for ${grant.grantee.display}`}>
                        {[["audit", "Audit trail"], ["denials", "Recent denials"]].map(([id, label]) => (
                            <button key={id} type="button" role="tab" aria-selected={kind === id} className={kind === id ? primaryButtonClass : buttonClass} onClick={() => setKind(id)}>{label}</button>
                        ))}
                    </div>
                    <DrilldownList key={`${grant.id}-${kind}`} kind={kind} grant={grant} />
                </>
            )}
        </div>
    );
}

function CandidateList({ busy, rows, total, next, selectedId, getId, render, onSelect, onMore, degraded = false, error = "" }) {
    return (
        <div aria-busy={busy} className="grid gap-[4px] border border-[color:var(--line-soft)] p-[6px]">
            <span className={NOTE_CLASS}>Showing {formatCompact(rows.length)} of {formatCompact(total)}</span>
            {rows.map((row) => {
                const id = getId(row);
                return (
                    <label key={id} className="flex cursor-pointer items-start gap-[7px] border-t border-[color:var(--line-soft)] py-[5px] text-[11px]">
                        <input type="radio" checked={selectedId === id} onChange={() => onSelect(row)} />
                        <span className="min-w-0 flex-1">{render(row)}</span>
                    </label>
                );
            })}
            {error ? <span className="text-[11px] text-red-300">{error}</span> : null}
            {!busy && !degraded && !error && !rows.length ? <span className={NOTE_CLASS}>No matches.</span> : null}
            <LoadingMore loading={busy} nextPageToken={next} shown={rows.length} total={total} onLoad={onMore} />
        </div>
    );
}

function groupDisplayName(group, annotations = []) {
    return annotations.find((entry) => groupKeyId(entry.group) === groupKeyId(group))?.displayName
        || group.displayName || group.sourceId;
}

function GrantEditor({ grant, initialGranteeType = "viewer", onCancel, onSaved }) {
    const capsRef = useLatestRef(useMyPermissions());
    const editing = Boolean(grant);
    const [granteeType, setGranteeType] = useState(grant?.grantee.type ?? initialGranteeType);
    const [selectedViewer, setSelectedViewer] = useState(grant?.grantee.type === "viewer" ? {
        viewerUserId: grant.grantee.viewerUserId,
        displayName: grant.grantee.display,
        hasSubjectMapping: true,
    } : null);
    const [selectedGranteeGroup, setSelectedGranteeGroup] = useState(grant?.grantee.type === "group" ? {
        ...grant.grantee.directoryGroup,
        displayName: grant.grantee.display,
        memberCount: grant.granteeMemberCount,
    } : null);
    const [roleId, setRoleId] = useState("");
    const [selectedGroups, setSelectedGroups] = useState(() => new Map(
        (grant?.scopeGroups ?? []).map((group) => [groupKeyId(group), { ...group, displayName: groupDisplayName(group, grant?.scopeAnnotations) }]),
    ));
    const [recipientGroupSearch, setRecipientGroupSearch] = useState("");
    const [scopeGroupSearch, setScopeGroupSearch] = useState("");
    const [viewerSearch, setViewerSearch] = useState("");
    const [recipientGroups, setRecipientGroups] = useState({ loading: !editing, rows: [], next: "", total: 0, degraded: false, error: "" });
    const [scopeCandidates, setScopeCandidates] = useState({ loading: true, rows: [], next: "", total: 0, degraded: false, error: "" });
    const [viewers, setViewers] = useState({ loading: !editing, rows: [], next: "", total: 0, error: "" });
    const [roles, setRoles] = useState({ loading: !editing, rows: [], next: "", total: 0, error: "" });
    const [impact, setImpact] = useState({ loading: false, value: null, error: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const recipientGroupRequest = useRef(0);
    const scopeGroupRequest = useRef(0);
    const viewerRequest = useRef(0);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const loadRecipientGroups = useCallback(async (token = "") => {
        if (editing || granteeType !== "group") return;
        const request = ++recipientGroupRequest.current;
        setRecipientGroups((current) => token
            ? { ...current, loading: true, error: "" }
            : { loading: true, rows: [], next: "", total: 0, degraded: false, error: "" });
        try {
            const page = await listGrantGroups({ search: recipientGroupSearch, pageSize: PAGE_SIZE, pageToken: token });
            setRecipientGroups((current) => recipientGroupRequest.current === request
                ? {
                    loading: false,
                    rows: token ? [...current.rows, ...page.groups] : page.groups,
                    next: page.nextPageToken,
                    total: page.totalCount,
                    degraded: page.degraded,
                    error: "",
                }
                : current);
        } catch (failure) {
            const message = denialOrRawMessage(failure, capsRef.current);
            setRecipientGroups((current) => recipientGroupRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    }, [capsRef, editing, granteeType, recipientGroupSearch]);
    useEffect(() => {
        if (editing || granteeType !== "group") return undefined;
        const timer = setTimeout(() => loadRecipientGroups(), 180);
        return () => {
            clearTimeout(timer);
            recipientGroupRequest.current += 1;
        };
    }, [editing, granteeType, loadRecipientGroups]);

    const loadScopeGroups = useCallback(async (token = "") => {
        const request = ++scopeGroupRequest.current;
        setScopeCandidates((current) => token
            ? { ...current, loading: true, error: "" }
            : { loading: true, rows: [], next: "", total: 0, degraded: false, error: "" });
        try {
            const page = await listGrantGroups({ search: scopeGroupSearch, pageSize: PAGE_SIZE, pageToken: token });
            setScopeCandidates((current) => scopeGroupRequest.current === request
                ? {
                    loading: false,
                    rows: token ? [...current.rows, ...page.groups] : page.groups,
                    next: page.nextPageToken,
                    total: page.totalCount,
                    degraded: page.degraded,
                    error: "",
                }
                : current);
        } catch (failure) {
            const message = denialOrRawMessage(failure, capsRef.current);
            setScopeCandidates((current) => scopeGroupRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    }, [capsRef, scopeGroupSearch]);
    useEffect(() => {
        const timer = setTimeout(() => loadScopeGroups(), 180);
        return () => {
            clearTimeout(timer);
            scopeGroupRequest.current += 1;
        };
    }, [loadScopeGroups]);

    const loadViewers = useCallback(async (token = "") => {
        if (editing || granteeType !== "viewer") return;
        const request = ++viewerRequest.current;
        setViewers((current) => token
            ? { ...current, loading: true, error: "" }
            : { loading: true, rows: [], next: "", total: 0, error: "" });
        try {
            const page = await listGrantViewers({ pageSize: PAGE_SIZE, pageToken: token, search: viewerSearch });
            setViewers((current) => viewerRequest.current === request
                ? { loading: false, rows: token ? [...current.rows, ...page.viewers] : page.viewers, next: page.nextPageToken, total: page.totalCount, error: "" }
                : current);
        } catch (failure) {
            const message = denialOrRawMessage(failure, capsRef.current);
            setViewers((current) => viewerRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    }, [capsRef, editing, granteeType, viewerSearch]);
    useEffect(() => {
        if (editing || granteeType !== "viewer") return undefined;
        const timer = setTimeout(() => loadViewers(), 180);
        return () => {
            clearTimeout(timer);
            viewerRequest.current += 1;
        };
    }, [editing, granteeType, loadViewers]);

    const loadRoles = useCallback(async (token = "") => {
        if (editing) return;
        setRoles((current) => ({ ...current, loading: true, error: "" }));
        try {
            const page = await listGrantRoles({ pageSize: 50, pageToken: token });
            setRoles((current) => ({ loading: false, rows: token ? [...current.rows, ...page.roles] : page.roles, next: page.nextPageToken, total: page.totalCount, error: "" }));
        } catch (failure) {
            setRoles((current) => ({ ...current, loading: false, error: denialOrRawMessage(failure, capsRef.current) }));
        }
    }, [capsRef, editing]);
    useEffect(() => { if (!editing) loadRoles(); }, [editing, loadRoles]);

    const granteeValue = useMemo(() => {
        if (granteeType === "viewer" && selectedViewer) return { type: "viewer", viewerUserId: selectedViewer.viewerUserId };
        if (granteeType === "group" && selectedGranteeGroup) return { type: "group", directoryGroup: wireDirectoryGroup(selectedGranteeGroup) };
        return null;
    }, [granteeType, selectedGranteeGroup, selectedViewer]);
    const scopeGroups = useMemo(() => [...selectedGroups.values()].map(wireDirectoryGroup), [selectedGroups]);
    const viewerNeedsMapping = granteeType === "viewer" && selectedViewer && !selectedViewer.hasSubjectMapping;

    useEffect(() => {
        if (!granteeValue || !scopeGroups.length) {
            setImpact({ loading: false, value: null, error: "" });
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            setImpact({ loading: true, value: null, error: "" });
            estimateGrantImpact({ grantee: granteeValue, scopeGroups }).then(
                (value) => { if (!cancelled) setImpact({ loading: false, value, error: "" }); },
                (failure) => { if (!cancelled) setImpact({ loading: false, value: null, error: denialOrRawMessage(failure, capsRef.current) }); },
            );
        }, 180);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [capsRef, granteeValue, scopeGroups]);

    const toggleScope = (group) => {
        const key = groupKeyId(group);
        setSelectedGroups((current) => {
            const next = new Map(current);
            if (next.has(key)) next.delete(key);
            else next.set(key, group);
            return next;
        });
    };
    const canSave = Boolean(
        !saving
        && granteeValue
        && (editing || roleId)
        && scopeGroups.length
        && !recipientGroups.degraded
        && !scopeCandidates.degraded
        && impact.value
        && !impact.value.degraded
        && impact.value.granteeCount > 0
        && impact.value.subjectCount > 0,
    );
    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        setError("");
        const operationId = newOperationId();
        try {
            if (editing) {
                await updateGrantScope({ bindingTable: grant.bindingTable, bindingId: grant.id, scopeGroups, operationId });
            } else {
                await createGrant({ grantee: granteeValue, roleId, scopeGroups, operationId });
            }
            if (!mounted.current) return;
            invalidateMyPermissions();
            onSaved(granteeType);
        } catch (failure) {
            if (!mounted.current) return;
            setError(denialOrRawMessage(failure, capsRef.current));
            setSaving(false);
        }
    };

    const selectedGranteeId = granteeType === "viewer"
        ? selectedViewer?.viewerUserId
        : groupKeyId(selectedGranteeGroup);
    return (
        <Panel eyebrow={editing ? "Edit grant" : "New grant"} title={editing ? `Edit ${grant.grantee.display}` : "Grant activity access"} busy={saving} action={<button type="button" className={buttonClass} onClick={onCancel} disabled={saving}>Cancel</button>}>
            {!editing ? (
                <div className="flex gap-[6px]" role="tablist" aria-label="Grant recipient type">
                    <button type="button" role="tab" aria-selected={granteeType === "group"} className={granteeType === "group" ? primaryButtonClass : buttonClass} onClick={() => setGranteeType("group")}>Group</button>
                    <button type="button" role="tab" aria-selected={granteeType === "viewer"} className={granteeType === "viewer" ? primaryButtonClass : buttonClass} onClick={() => setGranteeType("viewer")}>User</button>
                </div>
            ) : (
                <span className="text-[11px] text-[color:var(--muted)]">User or group and role stay unchanged: {grant.grantee.display} - {roleLabel(grant.roleSlug)}</span>
            )}

            {granteeType === "group" ? (
                <p className="m-0 text-[11px] leading-[1.5] text-[color:var(--muted)]">
                    Give every member of a group access with one rule. After directory sync, new members receive access and people who leave lose access through this rule. Other grants still apply.
                    {" "}Members need a dashboard account linked to their directory identity to use this access.
                </p>
            ) : null}

            <div data-grant-picker-workspace className="@container max-h-[420px] overflow-y-auto pr-[4px]">
                <div className={`grid items-start gap-[10px] ${editing ? "" : "@min-[880px]:grid-cols-2"}`}>
                    {!editing ? (
                        <section data-grant-recipient-picker className="grid gap-[7px]">
                            <span className={EYEBROW_CLASS}>{granteeType === "group" ? "1. Group receiving access" : "User receiving access"}</span>
                            {granteeType === "viewer" ? (
                                <>
                                    <input className={inputClass} type="search" value={viewerSearch} onChange={(event) => setViewerSearch(event.target.value)} placeholder="Search users..." aria-label="Search grant recipients" />
                                    <CandidateList
                                        busy={viewers.loading}
                                        rows={viewers.rows}
                                        total={viewers.total}
                                        next={viewers.next}
                                        selectedId={selectedGranteeId}
                                        getId={(viewer) => viewer.viewerUserId}
                                        onSelect={setSelectedViewer}
                                        onMore={() => loadViewers(viewers.next)}
                                        error={viewers.error}
                                        render={(viewer) => <span>{viewer.displayName || viewer.viewerUserId}{!viewer.hasSubjectMapping ? <span className="ml-[6px]"><Badge tone="warn">Employee not linked</Badge></span> : null}</span>}
                                    />
                                </>
                            ) : (
                                <>
                                    <input className={inputClass} type="search" value={recipientGroupSearch} onChange={(event) => setRecipientGroupSearch(event.target.value)} placeholder="Search groups receiving access..." aria-label="Search group recipients" />
                                    {recipientGroups.degraded ? <Badge tone="warn">Directory data unavailable</Badge> : null}
                                    <CandidateList
                                        busy={recipientGroups.loading}
                                        rows={recipientGroups.rows}
                                        total={recipientGroups.total}
                                        next={recipientGroups.next}
                                        selectedId={selectedGranteeId}
                                        getId={groupKeyId}
                                        onSelect={setSelectedGranteeGroup}
                                        onMore={() => loadRecipientGroups(recipientGroups.next)}
                                        degraded={recipientGroups.degraded}
                                        error={recipientGroups.error}
                                        render={(group) => <span>{group.displayName} - {countNoun(group.memberCount, "person", "people")}</span>}
                                    />
                                </>
                            )}
                        </section>
                    ) : null}

                    <section data-grant-scope-picker className="grid gap-[7px]">
                        <label className="grid gap-[4px] text-[11px] text-[color:var(--muted)]">
                            {granteeType === "group" ? `${editing ? "" : "2. "}Groups whose activity they can see` : "Visible employees and groups"}
                            <input className={inputClass} type="search" value={scopeGroupSearch} onChange={(event) => setScopeGroupSearch(event.target.value)} placeholder="Search visible groups..." aria-label="Search visible groups" />
                        </label>
                        <div aria-busy={scopeCandidates.loading} className="grid gap-[4px] border border-[color:var(--line-soft)] p-[6px]">
                            <span className={NOTE_CLASS}>Showing {formatCompact(scopeCandidates.rows.length)} of {formatCompact(scopeCandidates.total)} groups - {selectedGroups.size} selected</span>
                            {scopeCandidates.degraded ? <span className="text-[11px] text-[color:var(--warn,#8a6000)]">Directory data is temporarily unavailable. Try again before saving.</span> : null}
                            {scopeCandidates.error ? <span className="text-[11px] text-red-300">{scopeCandidates.error}</span> : null}
                            {scopeCandidates.rows.map((group) => {
                                const id = groupKeyId(group);
                                return (
                                    <label key={id} className="flex cursor-pointer items-start gap-[7px] border-t border-[color:var(--line-soft)] py-[5px] text-[11px]">
                                        <input type="checkbox" checked={selectedGroups.has(id)} onChange={() => toggleScope(group)} />
                                        <span>{group.displayName} - {countNoun(group.memberCount, "person", "people")}</span>
                                    </label>
                                );
                            })}
                            <LoadingMore loading={scopeCandidates.loading} nextPageToken={scopeCandidates.next} shown={scopeCandidates.rows.length} total={scopeCandidates.total} onLoad={() => loadScopeGroups(scopeCandidates.next)} />
                        </div>
                    </section>
                </div>
            </div>

            {!editing ? (
                <label className="grid gap-[4px] text-[11px] text-[color:var(--muted)]">
                    {granteeType === "group" ? "3. Role for this access" : "Role"}
                    <select className={inputClass} value={roleId} onChange={(event) => setRoleId(event.target.value)} disabled={roles.loading}>
                        <option value="">Choose a role...</option>
                        {roles.rows.map((role) => <option key={role.roleId} value={role.roleId}>{roleLabel(role.roleSlug)}</option>)}
                    </select>
                    <span className={NOTE_CLASS}>Grants include only permissions limited to the selected groups. Tenant-wide administration is excluded. Showing {formatCompact(roles.rows.length)} of {formatCompact(roles.total)} assignable roles.</span>
                    {roles.error ? <span className="text-[11px] text-red-300">{roles.error}</span> : null}
                    <LoadingMore loading={roles.loading} nextPageToken={roles.next} shown={roles.rows.length} total={roles.total} onLoad={() => loadRoles(roles.next)} />
                </label>
            ) : null}

            {selectedGroups.size ? (
                <div className="flex flex-wrap gap-[5px]">
                    {[...selectedGroups.entries()].map(([id, group]) => (
                        <button key={id} type="button" className={buttonClass} onClick={() => toggleScope(group)} title="Remove group">
                            {group.displayName || group.sourceId} x
                        </button>
                    ))}
                </div>
            ) : null}

            <div aria-busy={impact.loading} className="grid gap-[4px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] p-[9px]">
                <span className={EYEBROW_CLASS}>Before you save</span>
                {granteeType === "group" && selectedGranteeGroup && selectedGroups.size > 0 ? (
                    <span className="text-[11px] text-[color:var(--ink)]">
                        Members of <strong>{selectedGranteeGroup.displayName || selectedGranteeGroup.sourceId}</strong> receive access to activity from <strong>{[...selectedGroups.values()].map((group) => group.displayName || group.sourceId).join(", ")}</strong>.
                    </span>
                ) : null}
                {impact.loading ? <span className={NOTE_CLASS}>Calculating exact impact...</span> : impact.value?.degraded ? (
                    <><Badge tone="warn">Impact unavailable</Badge><span className={NOTE_CLASS}>Wait for directory data to recover before changing access.</span></>
                ) : impact.value ? (
                    <>
                        <strong className="text-[11px] font-medium text-[color:var(--ink)]">
                            {viewerNeedsMapping ? "After this dashboard account is linked, " : ""}{countNoun(impact.value.granteeCount, "user")} will be able to see activity for {countNoun(impact.value.subjectCount, "employee")}
                        </strong>
                        <span className={NOTE_CLASS}>The selected role still controls which activity they can open.</span>
                        {viewerNeedsMapping ? <span className={NOTE_CLASS}>Link this dashboard account to an employee before they can use the grant.</span> : null}
                        {impact.value.granteeCount === 0 || impact.value.subjectCount === 0 ? <Badge tone="danger">No one receives access</Badge> : null}
                    </>
                ) : <span className={NOTE_CLASS}>Choose a user or group and at least one visible group.</span>}
                {impact.error ? <span className="text-[11px] text-red-300">{impact.error}</span> : null}
            </div>
            <span className={NOTE_CLASS}>Most grant changes take effect within a few seconds. Group membership changes take effect after the next directory sync.</span>
            {error ? <span className="text-[11px] text-red-300">{error}</span> : null}
            <div className="flex justify-end gap-[6px]">
                <button type="button" className={buttonClass} onClick={onCancel} disabled={saving}>Cancel</button>
                <button type="button" className={primaryButtonClass} onClick={save} disabled={!canSave}>{saving ? "Saving..." : editing ? "Save scope" : "Create grant"}</button>
            </div>
        </Panel>
    );
}

// When impactDegraded is set, the server could not count group members and left
// every count at 0. Presence comes from a separate read and stays accurate.
function groupRecipientHealth(grant) {
    if (grant.grantee.type !== "group") return null;
    if (grant.granteePresence === "absent") return { text: "Not in directory", tone: "danger" };
    if (grant.granteePresence === "live" && !grant.impactDegraded && grant.granteeMemberCount === 0) return { text: "No active members", tone: "danger" };
    return null;
}

function scopeBadges(grant) {
    const badges = [];
    if (grant.impactDegraded) badges.push({ text: "Count unavailable", tone: "warn" });
    for (const annotation of grant.scopeAnnotations) {
        if (annotation.presence === "absent") badges.push({ text: `${annotation.displayName || annotation.group?.sourceId || "Group"}: Not in directory`, tone: "danger" });
        else if (annotation.presence === "live" && !grant.impactDegraded && annotation.memberCount === 0) badges.push({ text: `${annotation.displayName || annotation.group?.sourceId || "Group"}: No active members`, tone: "danger" });
    }
    if (!grant.impactDegraded && grant.effectiveSubjectCount === 0 && !groupRecipientHealth(grant) && !badges.length) badges.push({ text: "No visible employees", tone: "danger" });
    return badges;
}

function GrantBadgeList({ badges }) {
    if (!badges.length) return null;
    return <div className="flex flex-wrap gap-[4px]">{badges.map((badge, index) => <Badge key={`${badge.text}-${index}`} tone={badge.tone}>{badge.text}</Badge>)}</div>;
}

export default function AccessTab({ selectedGrant = null, onClearSelectedGrant }) {
    const caps = useMyPermissions();
    const capsRef = useLatestRef(caps);
    const canManage = hasSensitivePermission(caps, PERMISSIONS.GRANTS_MANAGE);
    const canBrowsePeople = hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    const canEdit = canManage && canBrowsePeople;
    const [grantSearchInput, setGrantSearchInput] = useState("");
    const [grantSearch, setGrantSearch] = useState("");
    const [grantType, setGrantType] = useState(selectedGrant?.grantee.type === "viewer" ? "users" : "groups");
    const [refresh, setRefresh] = useState(0);
    const [state, setState] = useState({ loading: true, rows: [], next: "", total: 0, error: "" });
    const [editor, setEditor] = useState(null);
    const editorRef = useRef(null);
    const [expanded, setExpanded] = useState("");
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revoking, setRevoking] = useState(false);
    const [revokeError, setRevokeError] = useState("");
    const listRequest = useRef(0);
    useEffect(() => {
        const timer = setTimeout(() => setGrantSearch(grantSearchInput.trim()), 220);
        return () => clearTimeout(timer);
    }, [grantSearchInput]);
    useEffect(() => {
        if (!editor) return;
        editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
        editorRef.current?.focus?.({ preventScroll: true });
    }, [editor]);
    useEffect(() => {
        if (canEdit) return;
        setEditor(null);
        if (!canManage) setRevokeTarget(null);
    }, [canEdit, canManage]);
    const bindingTable = grantType === "groups"
        ? "GRANT_BINDING_TABLE_DIRECTORY_GROUP_BINDING"
        : "GRANT_BINDING_TABLE_ROLE_BINDING";

    useEffect(() => {
        const request = ++listRequest.current;
        setState({ loading: true, rows: [], next: "", total: 0, error: "" });
        listGrants({ pageSize: PAGE_SIZE, search: grantSearch, bindingTable }).then(
            (page) => { if (listRequest.current === request) setState({ loading: false, rows: page.grants, next: page.nextPageToken, total: page.totalCount, error: "" }); },
            (error) => { if (listRequest.current === request) setState({ loading: false, rows: [], next: "", total: 0, error: denialOrRawMessage(error, capsRef.current) }); },
        );
        return () => { listRequest.current += 1; };
    }, [bindingTable, capsRef, grantSearch, refresh]);

    const loadMore = async () => {
        if (!state.next || state.loading) return;
        const request = ++listRequest.current;
        setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const page = await listGrants({ pageSize: PAGE_SIZE, pageToken: state.next, search: grantSearch, bindingTable });
            setState((current) => listRequest.current === request
                ? { loading: false, rows: [...current.rows, ...page.grants], next: page.nextPageToken, total: page.totalCount, error: "" }
                : current);
        } catch (error) {
            const message = denialOrRawMessage(error, capsRef.current);
            setState((current) => listRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    };
    const afterSave = (granteeType) => {
        if (granteeType) setGrantType(granteeType === "group" ? "groups" : "users");
        if (editor?.mode === "create") {
            setGrantSearchInput("");
            setGrantSearch("");
        }
        setEditor(null);
        onClearSelectedGrant?.();
        setExpanded("");
        setRefresh((value) => value + 1);
    };
    const revoke = async () => {
        if (!revokeTarget || revoking) return;
        setRevoking(true);
        setRevokeError("");
        const operationId = newOperationId();
        try {
            await revokeGrant({ bindingTable: revokeTarget.bindingTable, bindingId: revokeTarget.id, operationId });
            invalidateMyPermissions();
            setRevokeTarget(null);
            setRevoking(false);
            afterSave();
        } catch (error) {
            setRevokeError(denialOrRawMessage(error, capsRef.current));
            setRevoking(false);
        }
    };

    return (
        <>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-[var(--main-inset)] py-[12px]">
            <div className="mx-auto grid min-w-0 w-full max-w-[1180px] gap-[12px] pb-[24px]">
                <div className="flex flex-wrap items-start justify-between gap-[12px]">
                    <div className="grid max-w-[720px] gap-[3px]">
                        <span className={EYEBROW_CLASS}>Activity access</span>
                        <span className="text-[11px] text-[color:var(--ink)]">Give groups or individual users access to employee activity.</span>
                    </div>
                    {canEdit && !editor ? (
                        <button type="button" className={primaryButtonClass} onClick={() => setEditor({ mode: "create", granteeType: "group" })}>Add grant</button>
                    ) : null}
                </div>

                {selectedGrant && canBrowsePeople && !editor ? (
                    <Panel eyebrow="Selected grant" title={selectedGrant.grantee.display} action={<button type="button" className={buttonClass} onClick={onClearSelectedGrant}>Back to all grants</button>}>
                        <p className="m-0 text-[15px] leading-[1.6] text-[color:var(--ink)]">
                            {selectedGrant.grantee.type === "group" ? "Group members have" : "This user has"} <strong>{roleLabel(selectedGrant.roleSlug)}</strong> access to activity from <strong>{selectedGrant.scopeGroups.map((group) => groupDisplayName(group, selectedGrant.scopeAnnotations)).join(", ")}</strong>.
                        </p>
                        <span className={NOTE_CLASS}>Configured assignment. Current visibility also depends on other grants, roles, and whether group restrictions are enabled.</span>
                        {canEdit ? <button type="button" className={`${buttonClass} justify-self-start`} onClick={() => setEditor({ mode: "edit", grant: selectedGrant })}>Edit grant</button> : null}
                        <GrantDrilldown grant={selectedGrant} />
                    </Panel>
                ) : null}

                {canEdit && editor ? (
                    <div ref={editorRef} tabIndex={-1} className="scroll-mt-[12px] outline-none" data-grant-editor-region>
                        <GrantEditor
                            key={editor.grant?.id ?? editor.granteeType ?? grantType}
                            grant={editor.grant ?? null}
                            initialGranteeType={editor.granteeType ?? "group"}
                            onCancel={() => setEditor(null)}
                            onSaved={afterSave}
                        />
                    </div>
                ) : null}

                <Panel
                    eyebrow="Grants"
                    title={grantType === "groups" ? "Group grants" : "User grants"}
                    busy={state.loading}
                    action={(
                        <div className="flex items-center gap-[8px]">
                            <span className="text-[11px] text-[color:var(--muted)]">Showing {formatCompact(state.rows.length)} of {formatCompact(state.total)}</span>

                        </div>
                    )}
                >
                    <span className={NOTE_CLASS}>Group grants follow directory membership automatically. User grants apply to one dashboard account.</span>
                    <div className="flex gap-[6px]" role="tablist" aria-label="Grant recipient type">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={grantType === "groups"}
                            className={grantType === "groups" ? primaryButtonClass : buttonClass}
                            onClick={() => { setGrantType("groups"); setExpanded(""); }}
                        >
                            Groups
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={grantType === "users"}
                            className={grantType === "users" ? primaryButtonClass : buttonClass}
                            onClick={() => { setGrantType("users"); setExpanded(""); }}
                        >
                            Users
                        </button>
                    </div>
                    <input
                        className={inputClass}
                        type="search"
                        value={grantSearchInput}
                        onChange={(event) => setGrantSearchInput(event.target.value)}
                        placeholder="Search users, groups, roles, or visible groups..."
                        aria-label="Search access grants"
                    />
                    {state.error ? <span className="text-[11px] text-red-300">{state.error}</span> : null}
                    {!revokeTarget && revokeError ? <span className="text-[11px] text-red-300">{revokeError}</span> : null}
                    <div className="@container grid gap-[8px]">
                        <div className="hidden @min-[880px]:grid @min-[880px]:grid-cols-[minmax(170px,1.15fr)_minmax(145px,0.75fr)_minmax(180px,1.2fr)_104px_190px] gap-[8px] border-y border-[color:var(--line-soft)] px-[10px] py-[6px] text-[11px] font-medium text-[color:var(--muted)]">
                            <span>{grantType === "groups" ? "Group" : "User"}</span>
                            <span>Role</span>
                            <span>Visible groups</span>
                            <span>Employees</span>
                            <span>Actions</span>
                        </div>
                        {state.rows.map((grant) => (
                            <article key={grant.id} className="grid gap-[8px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] p-[10px]">
                                <div className="grid gap-[8px] @min-[880px]:grid-cols-[minmax(170px,1.15fr)_minmax(145px,0.75fr)_minmax(180px,1.2fr)_104px_190px] @min-[880px]:items-center">
                                    <ResponsiveField label={grant.grantee.type === "group" ? "Group" : "User"}>
                                        <div className="grid min-w-0 gap-[2px]">
                                            <strong className="truncate text-[11px] font-medium text-[color:var(--ink)]">{grant.grantee.display}</strong>
                                            {grant.grantee.type === "group" && !grant.impactDegraded ? <span className={NOTE_CLASS}><PersonCount value={grant.granteeMemberCount} label="group member" /></span> : null}
                                            <GrantBadgeList badges={[groupRecipientHealth(grant)].filter(Boolean)} />
                                        </div>
                                    </ResponsiveField>
                                    <ResponsiveField label="Role"><span className="block min-w-0 truncate whitespace-nowrap text-[11px] text-[color:var(--ink)]" title={grant.roleSlug}>{roleLabel(grant.roleSlug)}</span></ResponsiveField>
                                    <ResponsiveField label="Visible groups">
                                        <div className="grid min-w-0 gap-[3px]">
                                            <span className="text-[11px] text-[color:var(--ink)]">{grant.scopeGroups.map((group) => groupDisplayName(group, grant.scopeAnnotations)).join(", ") || "-"}</span>
                                            <GrantBadgeList badges={scopeBadges(grant)} />
                                        </div>
                                    </ResponsiveField>
                                    <ResponsiveField label="Employees"><span className="text-[color:var(--muted)]">{grant.impactDegraded ? "-" : <PersonCount value={grant.effectiveSubjectCount} label="visible employee" />}</span></ResponsiveField>
                                    <ResponsiveField label="Actions" valueClassName="flex @min-[880px]:justify-end">
                                      <div className="flex flex-wrap gap-[5px]">
                                        <button type="button" className={buttonClass} onClick={() => setExpanded(expanded === grant.id ? "" : grant.id)}>{expanded === grant.id ? "Close" : "Details"}</button>
                                        {canEdit ? <button type="button" className={buttonClass} onClick={() => setEditor({ mode: "edit", grant })}>Edit</button> : null}
                                        {canManage ? <button type="button" className={destructiveButtonClass} onClick={() => { setRevokeError(""); setRevokeTarget(grant); }}>Revoke</button> : null}
                                      </div>
                                    </ResponsiveField>
                                </div>
                                {expanded === grant.id ? <GrantDrilldown grant={grant} /> : null}
                            </article>
                        ))}
                        {!state.loading && !state.rows.length ? (
                            <div className="grid gap-[3px] border border-[color:var(--line-soft)] p-[12px]">
                                <strong className="text-[11px] font-medium text-[color:var(--ink)]">No grants found</strong>
                                <span className={NOTE_CLASS}>{grantSearch ? "Try a different user, group, role, or visible group." : `Create a grant for a ${grantType === "groups" ? "group" : "user"}.`}</span>
                            </div>
                        ) : null}
                    </div>
                    <LoadingMore loading={state.loading} nextPageToken={state.next} shown={state.rows.length} total={state.total} onLoad={loadMore} />
                </Panel>
            </div>
          </div>
          <ConfirmDialog
              open={Boolean(revokeTarget)}
              analyticsId={DIALOG.ACCESS_REVOKE}
              eyebrow="Revoke access"
              title={revokeTarget ? `Revoke grant for ${revokeTarget.grantee.display}?` : "Revoke grant?"}
              body={(
                  <div className="grid gap-[7px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                      <p className="m-0">This removes the grant immediately. The change will be recorded in the audit trail.</p>
                      {revokeError ? <span role="alert" className="text-[color:var(--red,#c0392b)]">{revokeError}</span> : null}
                  </div>
              )}
              confirmLabel={revoking ? "Revoking..." : "Revoke grant"}
              confirmDisabled={revoking}
              destructive
              onConfirm={revoke}
              onCancel={() => {
                  if (revoking) return;
                  setRevokeTarget(null);
                  setRevokeError("");
              }}
          />
        </>
    );
}
